import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronRight, AlertTriangle, Users, FileText } from 'lucide-react';
import type {
  SatisfactionDrillDownResponse,
  SatisfactionSurveyRow,
  CoachEngagementRow,
  DistributionBar,
  SatisfactionSentiment,
  CoachEngagementLevel,
} from '../../../../types';
import { cohortsApi } from '../../../../services/api/cohorts';
import { getAvatarColor } from '../../../../utils/avatarHelpers';
import { DataLibraryDrawer } from '../../../../components/DataLibrary/DataLibraryDrawer';
import type { GroupState } from '../../../../components/common/GroupPanel/GroupPanel';
import type { ActiveFilterRow } from '../../../../components/common/FilterPanel/FilterPanel';
import classes from './SatisfactionDrillDown.module.css';

// ============================================================================
// Types
// ============================================================================

type TabKey = 'overview' | 'participantSurveys' | 'coachSurveys';
type FilterKey = 'flagged' | 'all' | string; // string = groupId
type SurveyTypeFilter = 'all' | 'session' | 'midpoint' | 'endpoint';

interface SatisfactionDrillDownProps {
  cohortId: string;
  onAddTask?: (defaults: { title: string; description: string }) => void;
  onTabChange?: () => void;
}

// ============================================================================
// Constants
// ============================================================================


const SENTIMENT_LABELS: Record<SatisfactionSentiment, string> = {
  very_satisfied: 'Very Satisfied',
  satisfied: 'Satisfied',
  neither: 'Neutral',
  dissatisfied: 'Dissatisfied',
  very_dissatisfied: 'Very Dissatisfied',
};

const SENTIMENT_CLASSES: Record<SatisfactionSentiment, string> = {
  very_satisfied: classes.sentimentDarkGreen,
  satisfied: classes.sentimentGreen,
  neither: classes.sentimentOlive,
  dissatisfied: classes.sentimentAmber,
  very_dissatisfied: classes.sentimentRed,
};

const ENGAGEMENT_LABELS: Record<CoachEngagementLevel, string> = {
  very_high: 'Very High',
  high: 'High',
  average: 'Average',
  low: 'Low',
  very_low: 'Very Low',
};

const ENGAGEMENT_CLASSES: Record<CoachEngagementLevel, string> = {
  very_high: classes.engagementDarkGreen,
  high: classes.engagementGreen,
  average: classes.engagementOlive,
  low: classes.engagementAmber,
  very_low: classes.engagementRed,
};

const SURVEY_TYPE_LABELS: Record<SurveyTypeFilter, string> = {
  all: 'All Types',
  session: 'Session Surveys',
  midpoint: 'Midpoint',
  endpoint: 'Endpoint',
};

function formatDate(iso: string | null): string {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ============================================================================
// Bar Chart Component
// ============================================================================

const BAR_COLOR_MAP: Record<string, string> = {
  'Very Satisfied': classes.barFillDarkGreen,
  'Very High': classes.barFillDarkGreen,
  'Satisfied': classes.barFillGreen,
  'High': classes.barFillGreen,
  'Neutral': classes.barFillOlive,
  'Average': classes.barFillOlive,
  'Dissatisfied': classes.barFillAmber,
  'Low': classes.barFillAmber,
  'Very Dissatisfied': classes.barFillRed,
  'Very Low': classes.barFillRed,
};

function HorizontalBarChart({ bars, maxCount }: { bars: DistributionBar[]; maxCount: number }) {
  return (
    <div className={classes.barChart}>
      {bars.map((bar) => {
        const widthPct = maxCount > 0 ? (bar.count / maxCount) * 100 : 0;
        const fillClass = bar.count === 0
          ? classes.barFillGray
          : (BAR_COLOR_MAP[bar.label] ?? classes.barFillGray);

        return (
          <div key={bar.label} className={classes.barRow}>
            <span className={classes.barLabel}>{bar.label}</span>
            <div className={classes.barTrack}>
              <div
                className={`${classes.barFill} ${fillClass}`}
                style={{ width: `${Math.max(widthPct, bar.count > 0 ? 4 : 0)}%` }}
              />
            </div>
            <span className={classes.barCount}>{bar.count}</span>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SatisfactionDrillDown({ cohortId, onAddTask, onTabChange }: SatisfactionDrillDownProps) {
  const [data, setData] = useState<SatisfactionDrillDownResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [participantFilter, setParticipantFilter] = useState<FilterKey>('all');
  const [surveyTypeFilter, setSurveyTypeFilter] = useState<SurveyTypeFilter>('all');
  const [coachSessionFilter, setCoachSessionFilter] = useState<string>('all');
  const [expandedCoachRow, setExpandedCoachRow] = useState<string | null>(null);

  // Data Library drawer
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerLayoutKey, setDrawerLayoutKey] = useState(0);
  const [drawerTab, setDrawerTab] = useState('participants');
  const [drawerParticipantsConfig, setDrawerParticipantsConfig] = useState<
    Parameters<typeof DataLibraryDrawer>[0]['participantsConfig']
  >(undefined);
  const [drawerGroup, setDrawerGroup] = useState<GroupState | null>(null);
  // MED-764: Participant ID for auto-opening detail modal in Data Library
  const [drawerParticipantId, setDrawerParticipantId] = useState<string | undefined>(undefined);
  // MED-773: Filters for pre-applying group selection in Data Library
  const [drawerFilters, setDrawerFilters] = useState<ActiveFilterRow[] | undefined>(undefined);

  const openDataLibrary = useCallback((tab: TabKey, groupFilter?: ActiveFilterRow[]) => {
    setDrawerParticipantId(undefined);
    setDrawerFilters(groupFilter);
    switch (tab) {
      case 'overview':
        // MED-661: Navigate to Attendance tab grouped by participant
        setDrawerTab('attendance');
        setDrawerParticipantsConfig(undefined);
        setDrawerGroup([{ field: 'participant_full_name', order: 'alphabetical' }]);
        break;
      case 'participantSurveys':
        setDrawerTab('participant_mid_vs_end');
        setDrawerParticipantsConfig(undefined);
        setDrawerGroup(null);
        break;
      case 'coachSurveys':
        setDrawerTab('coach_post_session');
        setDrawerParticipantsConfig(undefined);
        setDrawerGroup(null);
        break;
    }
    setDrawerLayoutKey(k => k + 1);
    setDrawerOpened(true);
  }, []);

  // MED-764: Open Data Library with participant detail modal
  const handleViewParticipant = useCallback((participantId: string) => {
    setDrawerTab('participants');
    setDrawerParticipantsConfig(undefined);
    setDrawerGroup(null);
    setDrawerParticipantId(participantId);
    setDrawerLayoutKey(k => k + 1);
    setDrawerOpened(true);
  }, []);

  // When switching to Overview tab, force off 'flagged' filter
  const handleTabChange = useCallback((tab: TabKey) => {
    setActiveTab(tab);
    if (tab === 'overview' && participantFilter === 'flagged') {
      setParticipantFilter('all');
    }
    onTabChange?.();
  }, [participantFilter, onTabChange]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await cohortsApi.getSatisfactionDrillDown(cohortId);
      setData(result);
    } catch (err) {
      console.error('Failed to load satisfaction drill-down:', err);
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtered overview data (recomputed from raw rows when group filter is active)
  const filteredOverview = useMemo(() => {
    if (!data) return null;
    // "All Participants" => use pre-aggregated overview data as-is
    if (participantFilter === 'all' || participantFilter === 'flagged') {
      return data.overview;
    }
    // Group filter => recompute distributions from raw rows
    const groupRows = data.participantSurveys.rows.filter(r => r.groupId === participantFilter);
    const sentimentCounts: Record<SatisfactionSentiment, number> = {
      very_satisfied: 0, satisfied: 0, neither: 0, dissatisfied: 0, very_dissatisfied: 0,
    };
    for (const row of groupRows) {
      sentimentCounts[row.sentiment]++;
    }
    const distribution: DistributionBar[] = [
      { label: 'Very Satisfied', count: sentimentCounts.very_satisfied },
      { label: 'Satisfied', count: sentimentCounts.satisfied },
      { label: 'Neutral', count: sentimentCounts.neither },
      { label: 'Dissatisfied', count: sentimentCounts.dissatisfied },
      { label: 'Very Dissatisfied', count: sentimentCounts.very_dissatisfied },
    ];
    const flaggedRows = groupRows.filter(r => r.sentiment === 'neither' || r.sentiment === 'dissatisfied' || r.sentiment === 'very_dissatisfied');
    const flaggedCallout = flaggedRows.length > 0 ? {
      participantNames: [...new Set(flaggedRows.map(r => r.participantName))],
      sentiment: SENTIMENT_LABELS[flaggedRows[0].sentiment],
      quoteSnippet: flaggedRows[0].responseText
        ? `"${flaggedRows[0].responseText.length > 60 ? flaggedRows[0].responseText.slice(0, 60) + '...' : flaggedRows[0].responseText}"`
        : null,
    } : null;

    // Recompute coach feedback from individual ratings filtered by group
    const groupRatings = (data.engagementRatings ?? []).filter(r => r.groupId === participantFilter);
    const ratingCounts: Record<number, number> = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    for (const r of groupRatings) {
      const clamped = Math.max(1, Math.min(5, Math.round(r.rating)));
      ratingCounts[clamped]++;
    }
    const coachDistribution: DistributionBar[] = [
      { label: 'Very High', count: ratingCounts[5] },
      { label: 'High', count: ratingCounts[4] },
      { label: 'Average', count: ratingCounts[3] },
      { label: 'Low', count: ratingCounts[2] },
      { label: 'Very Low', count: ratingCounts[1] },
    ];

    // Find most recent coach report for this group
    const groupReportRows = data.coachSurveys.rows
      .filter(r => r.groupId === participantFilter && r.statusReportText)
      .sort((a, b) => b.sessionNumber - a.sessionNumber);
    let latestReport = null;
    if (groupReportRows.length > 0) {
      const latest = groupReportRows[0];
      const nameParts = latest.coachName.split(' ');
      const initials = nameParts.map(n => n.charAt(0).toUpperCase()).slice(0, 2).join('');
      const groupName = data.groups.find(g => g.id === participantFilter)?.name ?? 'Unknown';
      latestReport = {
        sessionTitle: `Session ${latest.sessionNumber} - ${groupName}`,
        sessionDate: latest.sessionDate,
        reportText: latest.statusReportText!,
        coachName: latest.coachName,
        coachAvatarUrl: latest.coachAvatarUrl,
        coachInitials: initials,
      };
    }

    return {
      participantSatisfaction: {
        distribution,
        flaggedCount: flaggedRows.length,
        flaggedCallout,
      },
      coachFeedback: {
        distribution: coachDistribution,
        latestReport,
      },
    };
  }, [data, participantFilter]);

  // Filtered participant survey rows
  const filteredParticipantRows = useMemo(() => {
    if (!data) return [];
    let rows = data.participantSurveys.rows;

    // Participant/group filter
    if (participantFilter === 'flagged') {
      rows = rows.filter(r => r.isFlagged || r.sentiment === 'neither' || r.sentiment === 'dissatisfied' || r.sentiment === 'very_dissatisfied');
    } else if (participantFilter !== 'all') {
      rows = rows.filter(r => r.groupId === participantFilter);
    }

    // Survey type filter
    if (surveyTypeFilter !== 'all') {
      rows = rows.filter(r => r.surveyType === surveyTypeFilter);
    }

    return rows;
  }, [data, participantFilter, surveyTypeFilter]);

  // Filtered coach rows
  const filteredCoachRows = useMemo(() => {
    if (!data) return [];
    if (coachSessionFilter === 'all') return data.coachSurveys.rows;
    const sessionNum = parseInt(coachSessionFilter, 10);
    return data.coachSurveys.rows.filter(r => r.sessionNumber === sessionNum);
  }, [data, coachSessionFilter]);

  if (loading) {
    return (
      <div className={classes.drillDown}>
        <div className={classes.loadingContainer}>
          <span>Loading satisfaction data...</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={classes.drillDown}>
        <div className={classes.emptyState}>Failed to load satisfaction data</div>
      </div>
    );
  }

  return (
    <>
    <DataLibraryDrawer
      opened={drawerOpened}
      onClose={() => setDrawerOpened(false)}
      defaultCohortId={cohortId}
      defaultTab={drawerTab}
      participantsConfig={drawerParticipantsConfig}
      layoutKey={drawerLayoutKey}
      defaultGroup={drawerGroup}
      defaultFilters={drawerFilters}
      defaultParticipantId={drawerParticipantId}
    />
    <div className={classes.drillDown}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <span className={classes.headerTitle}>{data.header.title}</span>
          {data.header.calloutText && (
            <span className={classes.calloutBadge}>{data.header.calloutText}</span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className={classes.tabs}>
        <button
          className={`${classes.tab} ${activeTab === 'overview' ? classes.tabActive : ''}`}
          onClick={() => handleTabChange('overview')}
        >
          Overview
        </button>
        <button
          className={`${classes.tab} ${activeTab === 'participantSurveys' ? classes.tabActive : ''}`}
          onClick={() => handleTabChange('participantSurveys')}
        >
          Participant Surveys
          {data.participantSurveys.flaggedCount > 0 && (
            <span className={classes.tabFlaggedCount}>{data.participantSurveys.flaggedCount}</span>
          )}
        </button>
        <button
          className={`${classes.tab} ${activeTab === 'coachSurveys' ? classes.tabActive : ''}`}
          onClick={() => handleTabChange('coachSurveys')}
        >
          Coach Surveys
        </button>
        <button
          className={classes.viewAllLink}
          style={{ marginLeft: 'auto' }}
          onClick={() => openDataLibrary(activeTab)}
        >
          View in Data Library <ChevronRight size={14} />
        </button>
      </div>

      {/* Filter Chips (shown on Overview and Participant Surveys tabs) */}
      {(activeTab === 'overview' || activeTab === 'participantSurveys') && (
        <div className={classes.filterChips}>
          {activeTab !== 'overview' && (
            <button
              className={`${classes.filterChip} ${participantFilter === 'flagged' ? classes.filterChipActive : ''}`}
              onClick={() => { setParticipantFilter('flagged'); onTabChange?.(); }}
            >
              <span className={classes.filterChipIcon}><AlertTriangle size={12} /></span>
              Flagged Only
            </button>
          )}
          <button
            className={`${classes.filterChip} ${participantFilter === 'all' ? classes.filterChipActive : ''}`}
            onClick={() => { setParticipantFilter('all'); onTabChange?.(); }}
          >
            All Participants
          </button>
          {data.groups.map(group => (
            <button
              key={group.id}
              className={`${classes.filterChip} ${participantFilter === group.id ? classes.filterChipActive : ''}`}
              onClick={() => { setParticipantFilter(group.id); onTabChange?.(); }}
            >
              <span className={classes.filterChipIcon}><Users size={12} /></span>
              {group.name}
            </button>
          ))}
          {/* MED-773: Secondary "View [Group] in Data Library" link */}
          {participantFilter !== 'all' && participantFilter !== 'flagged' && (() => {
            const selectedGroupName = data.groups.find(g => g.id === participantFilter)?.name;
            return selectedGroupName ? (
              <button
                className={classes.viewAllLink}
                style={{ marginLeft: 'auto' }}
                onClick={() => openDataLibrary(activeTab, [{ key: 'group', values: [participantFilter] }])}
              >
                View {selectedGroupName} in Data Library <ChevronRight size={14} />
              </button>
            ) : null;
          })()}
        </div>
      )}

      {/* Survey Type Filter (Participant Surveys tab only) */}
      {activeTab === 'participantSurveys' && (
        <div className={classes.filterChips}>
          {(['all', 'session', 'midpoint', 'endpoint'] as SurveyTypeFilter[]).map(type => (
            <button
              key={type}
              className={`${classes.filterChip} ${surveyTypeFilter === type ? classes.filterChipActive : ''}`}
              onClick={() => { setSurveyTypeFilter(type); onTabChange?.(); }}
            >
              {SURVEY_TYPE_LABELS[type]}
            </button>
          ))}
        </div>
      )}

      {/* Coach Surveys Session Filter */}
      {activeTab === 'coachSurveys' && (
        <div className={classes.filterChips}>
          <button
            className={`${classes.filterChip} ${coachSessionFilter === 'all' ? classes.filterChipActive : ''}`}
            onClick={() => { setCoachSessionFilter('all'); onTabChange?.(); }}
          >
            All Sessions
          </button>
          {data.coachSurveys.sessions.map(s => (
            <button
              key={s.sessionNumber}
              className={`${classes.filterChip} ${coachSessionFilter === String(s.sessionNumber) ? classes.filterChipActive : ''}`}
              onClick={() => { setCoachSessionFilter(String(s.sessionNumber)); onTabChange?.(); }}
            >
              Session {s.sessionNumber}
            </button>
          ))}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && filteredOverview && (
        <OverviewTabContent
          overview={filteredOverview}
          onViewSurveys={() => handleTabChange('participantSurveys')}
        />
      )}

      {activeTab === 'participantSurveys' && (
        <ParticipantSurveysTabContent
          rows={filteredParticipantRows}
          onAddTask={onAddTask}
          onViewParticipant={handleViewParticipant}
        />
      )}

      {activeTab === 'coachSurveys' && (
        <CoachSurveysTabContent
          rows={filteredCoachRows}
          expandedRow={expandedCoachRow}
          onToggleExpand={(id) => setExpandedCoachRow(expandedCoachRow === id ? null : id)}
        />
      )}
    </div>
    </>
  );
}

// ============================================================================
// Overview Tab
// ============================================================================

function OverviewTabContent({
  overview,
  onViewSurveys,
}: {
  overview: SatisfactionDrillDownResponse['overview'];
  onViewSurveys: () => void;
}) {
  const { participantSatisfaction, coachFeedback } = overview;
  const maxParticipantCount = Math.max(...participantSatisfaction.distribution.map(d => d.count), 1);
  const maxCoachCount = Math.max(...coachFeedback.distribution.map(d => d.count), 1);

  return (
    <div className={classes.overviewPanels}>
      {/* Left Panel: Participant Engagement */}
      <div className={classes.panel}>
        <div className={classes.panelHeader}>
          <div className={classes.panelTitle}>
            <span className={classes.panelTitleIcon}><Users size={16} /></span>
            Participant Engagement
          </div>
        </div>

        <HorizontalBarChart
          bars={coachFeedback.distribution}
          maxCount={maxCoachCount}
        />

        {coachFeedback.latestReport && (
          <div className={classes.latestReport}>
            <div className={classes.latestReportLabel}>
              <FileText size={14} />
              Latest Coach Report
            </div>
            <div className={classes.reportCard}>
              <div className={classes.reportSessionTitle}>
                {coachFeedback.latestReport.sessionTitle}
              </div>
              <div className={classes.reportDate}>
                {formatDate(coachFeedback.latestReport.sessionDate)}
              </div>
              <div className={classes.reportText}>
                {coachFeedback.latestReport.reportText}
              </div>
              <div className={classes.reportCoach}>
                <div
                  className={classes.coachAvatar}
                  style={{ background: getAvatarColor(coachFeedback.latestReport.coachName) }}
                >
                  {coachFeedback.latestReport.coachInitials}
                </div>
                <span className={classes.coachName}>{coachFeedback.latestReport.coachName}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Panel: Participant Satisfaction */}
      <div className={classes.panel}>
        <div className={classes.panelHeader}>
          <div className={classes.panelTitle}>
            <span className={classes.panelTitleIcon}><Users size={16} /></span>
            Participant Satisfaction
          </div>
          {participantSatisfaction.flaggedCount > 0 && (
            <span className={classes.neutralBadge}>
              {participantSatisfaction.flaggedCount} flagged
            </span>
          )}
        </div>

        <HorizontalBarChart
          bars={participantSatisfaction.distribution}
          maxCount={maxParticipantCount}
        />

        {participantSatisfaction.flaggedCallout && (
          <div className={classes.flaggedCallout}>
            <div className={classes.flaggedCalloutIcon}>
              <AlertTriangle size={14} />
            </div>
            <div className={classes.flaggedCalloutText}>
              <span className={classes.flaggedCalloutNames}>
                {participantSatisfaction.flaggedCallout.participantNames.join(', ')}
              </span>
              {' — '}{participantSatisfaction.flaggedCallout.sentiment}
              {participantSatisfaction.flaggedCallout.quoteSnippet && (
                <>, mentioned {participantSatisfaction.flaggedCallout.quoteSnippet}</>
              )}
            </div>
          </div>
        )}

        <button className={classes.viewSurveysLink} onClick={onViewSurveys}>
          View in Data Library <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Participant Surveys Tab
// ============================================================================

function ParticipantSurveysTabContent({
  rows,
  onAddTask,
  onViewParticipant,
}: {
  rows: SatisfactionSurveyRow[];
  onAddTask?: (defaults: { title: string; description: string }) => void;
  onViewParticipant?: (participantId: string) => void;
}) {
  if (rows.length === 0) {
    return <div className={classes.emptyState}>No survey responses match current filters</div>;
  }

  const handleAddTask = (row: SatisfactionSurveyRow) => {
    onAddTask?.({
      title: `Survey Flag: ${row.participantName}`,
      description: `${row.participantName} responded ${SENTIMENT_LABELS[row.sentiment]} on ${row.surveyLabel} survey${row.responseText ? `\n\nQuote: "${row.responseText}"` : ''}`,
    });
  };

  return (
    <table className={classes.table}>
      <thead className={classes.tableHead}>
        <tr>
          <th>Participant</th>
          <th>Group</th>
          <th>Survey Type</th>
          <th>Rating</th>
          <th>Date</th>
          <th></th>
        </tr>
      </thead>
      <tbody className={classes.tableBody}>
        {rows.map((row, idx) => (
          <ParticipantSurveyRowItem
            key={`${row.participantId}-${row.surveyType}-${idx}`}
            row={row}
            onAddTask={handleAddTask}
            onViewParticipant={onViewParticipant}
          />
        ))}
      </tbody>
    </table>
  );
}

function ParticipantSurveyRowItem({
  row,
  onAddTask,
  onViewParticipant,
}: {
  row: SatisfactionSurveyRow;
  onAddTask: (row: SatisfactionSurveyRow) => void;
  onViewParticipant?: (participantId: string) => void;
}) {
  const isFlaggedSentiment = row.sentiment === 'neither' || row.sentiment === 'dissatisfied' || row.sentiment === 'very_dissatisfied';

  return (
    <>
      <tr>
        <td>
          <div className={classes.participantCell}>
            <div
              className={classes.avatar}
              style={{ background: getAvatarColor(row.participantName) }}
            >
              {row.initials}
            </div>
            <span className={classes.participantName}>{row.participantName}</span>
          </div>
        </td>
        <td>{row.groupName ?? '--'}</td>
        <td>{row.surveyLabel}</td>
        <td>
          <span className={`${classes.sentimentPill} ${SENTIMENT_CLASSES[row.sentiment]}`}>
            {SENTIMENT_LABELS[row.sentiment]}
          </span>
        </td>
        <td>{formatDate(row.completedAt)}</td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isFlaggedSentiment && (
              <button className={classes.addTaskBtn} onClick={() => onAddTask(row)}>
                Add Task
              </button>
            )}
            {onViewParticipant && (
              <button
                className={classes.viewAllLink}
                onClick={() => onViewParticipant(row.participantId)}
              >
                view <ChevronRight size={14} />
              </button>
            )}
          </div>
        </td>
      </tr>
      {row.responseText && (
        <tr className={classes.quoteRow}>
          <td colSpan={6}>
            <span className={classes.quoteText}>
              &ldquo;{row.responseText.length > 120 ? `${row.responseText.slice(0, 120)}...` : row.responseText}&rdquo;
            </span>
          </td>
        </tr>
      )}
    </>
  );
}

// ============================================================================
// Coach Surveys Tab
// ============================================================================

function CoachSurveysTabContent({
  rows,
  expandedRow,
  onToggleExpand,
}: {
  rows: CoachEngagementRow[];
  expandedRow: string | null;
  onToggleExpand: (id: string) => void;
}) {
  if (rows.length === 0) {
    return <div className={classes.emptyState}>No coach engagement data available</div>;
  }

  return (
    <table className={classes.table}>
      <thead className={classes.tableHead}>
        <tr>
          <th></th>
          <th>Session</th>
          <th>Engagement Rating</th>
          <th>Coach</th>
          <th>Date</th>
        </tr>
      </thead>
      <tbody className={classes.tableBody}>
        {rows.map((row) => {
          const rowId = `${row.sessionId}-${row.coachId}`;
          const isExpanded = expandedRow === rowId;
          const isBelowAvg = row.engagementLevel === 'low' || row.engagementLevel === 'very_low';
          const nameParts = row.coachName.split(' ');
          const coachInitials = nameParts.map(n => n.charAt(0).toUpperCase()).slice(0, 2).join('');

          return (
            <CoachSurveyRowItem
              key={rowId}
              row={row}
              isExpanded={isExpanded}
              isBelowAvg={isBelowAvg}
              coachInitials={coachInitials}
              onToggle={() => onToggleExpand(rowId)}
            />
          );
        })}
      </tbody>
    </table>
  );
}

function CoachSurveyRowItem({
  row,
  isExpanded,
  isBelowAvg,
  coachInitials,
  onToggle,
}: {
  row: CoachEngagementRow;
  isExpanded: boolean;
  isBelowAvg: boolean;
  coachInitials: string;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={`${classes.expandableRow} ${isBelowAvg ? classes.flaggedRow : ''}`}
        onClick={onToggle}
      >
        <td style={{ width: 30 }}>
          {row.statusReportText && (
            <span className={`${classes.expandIcon} ${isExpanded ? classes.expandIconOpen : ''}`}>
              <ChevronRight size={14} />
            </span>
          )}
        </td>
        <td>
          Session {row.sessionNumber} — {row.sessionTitle}
        </td>
        <td>
          <span className={`${classes.engagementPill} ${ENGAGEMENT_CLASSES[row.engagementLevel]}`}>
            {ENGAGEMENT_LABELS[row.engagementLevel]}
          </span>
        </td>
        <td>
          <div className={classes.participantCell}>
            <div
              className={classes.coachAvatar}
              style={{ background: getAvatarColor(row.coachName) }}
            >
              {coachInitials}
            </div>
            <span>{row.coachName}</span>
          </div>
        </td>
        <td>{formatDate(row.sessionDate)}</td>
      </tr>
      {isExpanded && row.statusReportText && (
        <tr>
          <td colSpan={5}>
            <div className={classes.expandedContent}>
              <div className={classes.expandedText}>{row.statusReportText}</div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default SatisfactionDrillDown;
